import { useState, type ReactNode } from "react";
import type React from "react";

import { Menubar } from "primereact/menubar";
import { Button } from "primereact/button";

import { NavLogo } from "./NavLogo/NavLogo";
import { MobileNavDrawer, type MobileNavEntry } from "./MobileNav/MobileNavDrawer";

import S from "./UserNavBar.module.scss";

interface MobileNavBarProps {
  navbarRef?: React.RefObject<HTMLDivElement>;
  testId: string;
  thumbLogoUrl?: string;
  showMyJourneyCallout: boolean;
  navigateTo: (route: string, openInNewTab?: boolean) => void;
  navEntries: MobileNavEntry[];
  end: ReactNode;
  dialogTemplate?: (props: {
    showDialog: boolean;
    setShowDialog: (value: boolean) => void;
    onConfirm: () => void;
  }) => ReactNode;
  showDialog: boolean;
  setShowDialog: (value: boolean) => void;
  confirmNavigation: () => void;
}

/**
 * Nav used below 960px: a hamburger toggle + side drawer instead of the full
 * Menubar item list. UserNavBar only mounts this while isMobile is true, so
 * mobileNavOpen resets for free whenever the breakpoint is crossed.
 */
export function MobileNavBar({
  navbarRef,
  testId,
  thumbLogoUrl,
  showMyJourneyCallout,
  navigateTo,
  navEntries,
  end,
  dialogTemplate,
  showDialog,
  setShowDialog,
  confirmNavigation,
}: MobileNavBarProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <nav
      ref={(node: HTMLElement | null) => {
        if (navbarRef)
          (navbarRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node as HTMLDivElement | null;
      }}
      aria-label="Main navigation"
      data-testid={`${testId}-menubar`}
      className={S.mobileNavContainer}
    >
      <Menubar
        start={
          <Button
            icon="pi pi-bars"
            text
            rounded
            aria-label="Open navigation menu"
            aria-expanded={mobileNavOpen}
            aria-controls="mobile-nav-drawer"
            data-testid={`${testId}-mobile-toggle`}
            className={S.mobileNavToggle}
            onClick={() => setMobileNavOpen(true)}
          />
        }
        model={[]}
        end={end}
        className={S.menubar}
        pt={{
          menu: { className: "gap-1" },
          content: { className: "body-sm" },
          action: { className: "px-3" },
          button: { className: S.menubarButton },
          icon: { className: S.menubarIcon },
          end: { className: S.menubarEnd },
        }}
      />
      {/* Absolutely centered positioned  */}
      <div className={S.mobileNavLogo}>
        <NavLogo
          navigateTo={navigateTo}
          thumbLogoUrl={thumbLogoUrl}
          iconOnly
          showMyJourneyCallout={showMyJourneyCallout}
        />
      </div>
      <MobileNavDrawer
        visible={mobileNavOpen}
        onHide={() => setMobileNavOpen(false)}
        entries={navEntries}
        navigateTo={navigateTo}
      />
      {dialogTemplate?.({
        showDialog,
        setShowDialog,
        onConfirm: confirmNavigation,
      })}
    </nav>
  );
}
